<script setup>
import { ISourceMock, useSourceStore } from "@/store/Source/source-store";
import { computed, onMounted, reactive, ref } from "vue";
import { useLK } from "@/controllerLK/LKstore";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import { useNotificationsStore } from "@/store/notifications";
import SourceList from "@/components/SourcePage/SourceList.vue";
import SourceSettings from "@/components/SourcePage/SourceSettings.vue";
import { t } from "@/controllerLK/AvalLocalization";

const LKStore = useLK();
const store = useSourceStore();
const rubricStore = useRubricsStore();
const notificationStore = useNotificationsStore();


const sourceList = ref([]);
const originalSource = ref(null);
const activeSource = ref(null);
const isNewSourceCreating = ref(false);
const nextSource = ref(null);

const isShowUnsavedChangesPopup = ref(false);

onMounted(async () => {
  await rubricStore.getAllRubrics();
  if (!rubricStore.rubricsOnMonitoring.length) {
    window.routerLK.push("/");
    notificationStore.createNotify(t("notification_item.error.no_rubric"), "error");
  }
  sourceList.value = await store.getProjectSource(LKStore.project?.id);

  if (sourceList.value.length === 0) {
    addSource();
  } else {
    selectSource(sourceList.value[0]);
  }
});

// Добавление нового источника
const addSource = () => {
  if (isNewSourceCreating.value) {
    if (sourceList.value.length === 0) return false;
    selectSource(sourceList.value[0]);
    isNewSourceCreating.value = false;
  } else {
    const mock = JSON.parse(JSON.stringify(ISourceMock));
    selectSource(mock);
    isNewSourceCreating.value = true;
  }
};

// Создание источника
const createSource = async () => {
  await store.createProjectSource(LKStore.project.id, activeSource.value);
};

// Обновление источника
const updateSource = async () => {
  await store.updateProjectSource(LKStore.project.id, activeSource.value);
};

// Удаление источника
const isShowDeleteSourceModal = ref(false);

const deleteSource = async () => {
  isShowDeleteSourceModal.value = false;
  await store.deleteProjectSource(LKStore.project.id, activeSource.value);
  sourceList.value = await store.getProjectSource(LKStore.project?.id);

  if (sourceList.value.length === 0) {
    addSource();
  } else {
    selectSource(sourceList.value[0]);
  }
};

// Обработчик выбора источника
const handleSelectSource = (source) => {

  if (isNewSourceCreating.value) {
    isShowUnsavedChangesPopup.value = true;
    nextSource.value = JSON.parse(JSON.stringify(source));
    return false;
  }

  if (JSON.stringify(activeSource.value) !== JSON.stringify(originalSource.value)) {
    isShowUnsavedChangesPopup.value = true;
    nextSource.value = JSON.parse(JSON.stringify(source));
  } else {
    selectSource(source);
  }
};

// Выбор источника
const selectSource = (src) => {
  activeSource.value = JSON.parse(JSON.stringify(src));
  originalSource.value = JSON.parse(JSON.stringify(src));
};

// Сохранение изменений/создание источника с валидацией
const onSave = async () => {
  // errors.name = !activeSource.value.name;
  errors.url  = !activeSource.value.start_url;
  errors.type = !activeSource.value.type;
  errors.invalid_start_url = !isValidUrl(activeSource.value.start_url);

  if (errors.name || errors.url || errors.type || errors.invalid_start_url) {
    return;
  }

  if (isNewSourceCreating.value) {

    // Если имя не задано, то задаем из start url без https/http
    if (activeSource.value.name.length === 0) {
      activeSource.value.name = activeSource.value.start_url.replace(/^(https?:\/\/)?/, "");
    }

    await createSource();
    isNewSourceCreating.value = false;
    sourceList.value = await store.getProjectSource(LKStore.project?.id);
    selectSource(sourceList.value[sourceList.value.length - 1]);
  } else {
    await updateSource();
    selectSource(activeSource.value);
    sourceList.value = await store.getProjectSource(LKStore.project?.id);
  }
};

// Обработчик открытия окна несохраненных изменений
const handleShowUnsavedChangesPopup = () => {
  if (isNewSourceCreating.value) {
    if (nextSource.value === null) {
      addSource();
      isShowUnsavedChangesPopup.value = false;
      return false;
    }
  }

  selectSource(nextSource.value);
  nextSource.value = null;
  isNewSourceCreating.value = false;
  isShowUnsavedChangesPopup.value = false;
};

// Наличие изменений
const isSourceHasChanges = computed(() => {
  return JSON.stringify(activeSource.value) !== JSON.stringify(originalSource.value);
});

// Сброс формы источника к изначальному состоянию
const resetForm = () => {
  if (isNewSourceCreating.value) {
    isShowUnsavedChangesPopup.value = true;
  } else {
    activeSource.value = JSON.parse(JSON.stringify(originalSource.value));
  }
};

// Ошибки
const errors = reactive({
  name: false,
  url: false,
  type: false,
  language: false,
  invalid_start_url: false,
  invalid_feed_url: false
});

// Валидация для start url и feed url
const isValidUrl = (url) => {
  const pattern = new RegExp(
    "^(https?:\\/\\/)" + // Проверка на обязательный протокол (http/https)
    "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|" + // Доменное имя
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // или IP (v4) адрес
    "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // Путь
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // Строка запроса
    "(\\#[-a-z\\d_]*)?$", // Фрагмент URL
    "i"
  );
  return pattern.test(url);
};

// Обновление значение активного источника из дочерних компонентов
const onUpdateActiveSource = ({ field, value }) => {
  activeSource.value[field] = value;
};
</script>

<template>
  <div class="source">
    <!--  Список источников -->
    <SourceList
      :active-source="activeSource"
      :source-list="sourceList"
      :is-new-source-creating="isNewSourceCreating"
      @add-source="addSource"
      @handle-select-source="source => handleSelectSource(source)"
      @update-active-source-name="newName => activeSource.name = newName"
    />

    <!--  Настройки источника -->
    <SourceSettings
      v-if="activeSource"
      :errors="errors"
      :active-source="activeSource"
      :is-source-has-changes="isSourceHasChanges"
      :is-new-source-creating="isNewSourceCreating"
      @on-save="onSave"
      @open-delete-popup="isShowDeleteSourceModal = true"
      @reset-form="resetForm"
      @update-active-source="onUpdateActiveSource"
    />

    <!--  Модальное окно подтверждения изменений -->
    <Modal
      v-if="isShowUnsavedChangesPopup"
      teleport="#app"
      :title="t(`source_page.alert_popup.title`)"
      :confirm-text="t(`source_page.alert_popup.accept_text`)"
      @close="isShowUnsavedChangesPopup = false"
      @cancel="isShowUnsavedChangesPopup = false"
      @confirm="handleShowUnsavedChangesPopup"
    >
      {{ t('source_settings.modal.delete.message') }}
    </Modal>

    <!--  Модальное окно удаления источника  -->
    <Modal
      v-if="isShowDeleteSourceModal"
      teleport="#app"
      :title="t('source_settings.modal.delete.title')"
      :confirm-text="t('news_page_popup_delete.button.accept_delete')"
      @close="isShowDeleteSourceModal = false"
      @cancel="isShowDeleteSourceModal = false"
      @confirm="deleteSource"
    >
      {{ t('source_settings.modal.delete.message') }}
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.source {
  display: grid;
  grid-template-columns: 275px 1fr;
  grid-template-rows: 1fr;
  grid-gap: 10px;
  height: calc(100vh - 100px);
}
</style>